import type { AppStrings } from './types.js';
import { plural } from './plural.js';

export const fr: AppStrings = {
  // COMMON
  error_title: 'Erreur',
  cancel: 'Annuler',
  retry: 'Réessayer',
  back_arrow: '← Retour',

  // ASK SCREEN
  ask_box_unavailable: 'Box indisponible',
  ask_box_unavailable_desc: 'Le lien est peut-être obsolète ou le propriétaire a fermé son box.',
  ask_create_own_box: 'Créer mon propre box',
  ask_sent_title: 'Envoyé ✅',
  ask_sent_desc: 'Ta question anonyme a bien été transmise au propriétaire du box.',
  ask_anonymous_hint: 'Ta question sera envoyée de façon anonyme.',
  ask_placeholder: 'Écris ta question…',
  ask_send: 'Envoyer',
  ask_sending: 'Envoi…',
  ask_error_rate_limit: 'Trop de requêtes — attends une minute.',
  ask_error_restart: 'Merci de redémarrer la Mini App.',
  ask_error_load: 'Impossible de charger le box.',
  ask_error_send: 'Impossible d\'envoyer la question.',
  ask_error_banned: 'Tu es bloqué dans l\'app.',
  ask_banned_appeal_btn: 'Contester',
  ask_appeal_sent: 'Contestation envoyée. Nous l\'examinerons manuellement.',

  // AGE GATE
  age_gate_title: 'Réservé aux 18+',
  age_gate_desc:
    'Tu envoies une question anonyme. Sois respectueux : le harcèlement, les menaces, les contenus sexuels impliquant des mineurs et tout contenu illégal sont interdits. L\'anonymat ne signifie pas l\'impunité — les infractions sont signalées à la modération. En envoyant une question, tu confirmes avoir 18+ et acceptes les règles.',
  age_gate_confirm: 'J\'ai 18+ — continuer',

  // INBOX SCREEN
  inbox_title: 'Mon box',
  box_link_accordion: 'Lien d\'invitation',
  inbox_notifications_ask: '🔔 Autoriser le bot à t\'envoyer des notifications pour les nouvelles questions ?',
  inbox_notifications_allow: 'Autoriser',
  inbox_notifications_dismiss_label: 'Ignorer',
  inbox_box_toggle_disable: 'Désactiver les questions',
  inbox_box_toggle_enable: 'Activer les questions',
  inbox_box_disabled_warning: 'Les questions sont désactivées — aucune nouvelle question ne sera acceptée.',
  inbox_share_hint: 'Partage le lien pour que tes amis puissent te poser des questions anonymes.',
  inbox_copy: 'Copier',
  inbox_copied: 'Copié',
  inbox_copy_error: 'Erreur',
  inbox_no_deeplink: 'Le lien apparaîtra après le deploy.',
  inbox_invite_story: 'Inviter via Story',
  inbox_invite_chat: 'Inviter via chat',
  inbox_card_rendering: 'La carte d\'invitation est encore en cours de génération.',
  inbox_story_unsupported:
    'Les Stories ne sont pas disponibles dans cette version de Telegram. Essaie "via chat".',
  inbox_story_error: 'Impossible d\'ouvrir la Story.',
  inbox_chat_unsupported:
    'Le partage par chat n\'est pas disponible dans cette version de Telegram. Mets à jour ton client.',
  inbox_chat_error: 'Impossible de partager. Réessaie.',
  inbox_chat_prepare_error: 'Impossible de préparer l\'invitation.',
  inbox_filter_incoming: 'Reçues',
  inbox_filter_hidden: 'Masquées',
  inbox_filter_blocked: 'Bloquées',
  inbox_filter_empty: 'Rien ici pour l\'instant.',
  inbox_acc_unanswered: 'Sans réponse',
  inbox_acc_answered: 'Répondues',
  inbox_acc_hidden: 'Masquées',
  inbox_acc_blocked: 'Bloquées',
  inbox_empty: 'Pas encore de questions. Partage le lien ci-dessus.',
  inbox_load_more: 'Charger plus',
  inbox_load_more_error: 'Impossible de charger la page suivante.',
  inbox_error_load: 'Impossible de charger le box.',
  inbox_rate_limit: 'Trop de requêtes — attends une minute.',
  inbox_toggle_error: 'Impossible de changer le statut du box.',

  // MESSAGE BOX (Трек D)
  message_tagline: 'Écris-moi anonymement',
  message_placeholder: 'Écris un message…',
  message_anonymous_hint: 'Ton message sera envoyé de façon anonyme.',
  message_sent_title: 'Envoyé ✅',
  message_sent_desc: 'Ton message anonyme a bien été livré.',
  message_owner_entry: 'Écris-moi',
  message_share_hint: 'Partage le lien pour que n\'importe qui puisse t\'envoyer un message anonyme.',
  message_inbox_open: 'Messages reçus',
  message_inbox_title: 'Messages',
  message_inbox_empty: 'Pas encore de messages. Partage ton lien ci-dessus.',

  // ANSWER SCREEN
  answer_title: 'Répondre à la question',
  answer_placeholder: 'Écris ta réponse…',
  answer_submit: 'Envoyer',
  answer_submitting: 'Envoi…',
  answer_back: 'Retour',
  answer_error_send: 'Impossible d\'envoyer la réponse.',

  // SHARE SCREEN
  share_title: 'Réponse publiée ✅',
  share_hint: 'Partage la carte et reçois encore plus de questions.',
  share_card_rendering_desc:
    'La carte est encore en cours de génération. Reviens aux questions et réessaie dans un moment — tu pourras la partager en Story ou par chat.',
  share_card_alt: 'Carte de réponse',
  share_story_btn: 'Partager en Story',
  share_story_btn_busy: 'Ouverture…',
  share_chat_btn: 'Partager par chat',
  share_chat_btn_busy: 'Préparation…',
  share_back: 'Retour aux questions',
  share_story_unsupported:
    'Les Stories ne sont pas disponibles dans cette version de Telegram. Essaie "Partager par chat".',
  share_story_error: 'Impossible d\'ouvrir la Story. Essaie "Partager par chat".',
  share_chat_sent: 'Message envoyé.',
  share_chat_unsupported:
    'Le partage par chat n\'est pas disponible dans cette version de Telegram. Mets à jour ton client.',
  share_chat_error: 'Impossible de partager. Réessaie.',
  share_chat_prepare_error: 'Impossible de préparer le message.',

  // LEADERBOARD SCREEN
  lb_title: 'Classement',
  lb_period_7d: '7 jours',
  lb_period_30d: '30 jours',
  lb_period_all: 'Depuis toujours',
  lb_group_label: 'Catégorie',
  lb_group_all: 'Tous',
  lb_group_ask: 'Questions',
  lb_group_rate: 'Évaluations',
  lb_group_confession: 'Confessions',
  lb_group_guesswho: 'Qui est-ce',
  lb_group_message: 'Messages',
  lb_error: 'Impossible de charger le classement',
  lb_empty: 'Personne dans le classement pour l\'instant',
  lb_anon: 'Anonyme',
  lb_load_more: 'Charger plus',
  lb_questions: (n) => plural('fr', n, { one: '{n} question', other: '{n} questions' }),
  lb_referrals: (n) => plural('fr', n, { one: '{n} réf.', other: '{n} réfs.' }),

  // REWARDS SCREEN
  rw_title: 'Mes récompenses',
  rw_error: 'Impossible de charger les récompenses',
  rw_level: (n) => `Niveau ${n}`,
  rw_confirmed_referrals: (n) =>
    plural('fr', n, { one: '{n} filleul confirmé', other: '{n} filleuls confirmés' }),
  rw_next_level_section: (n) =>
    plural('fr', n, { one: 'NIVEAU SUIVANT — {n} filleul', other: 'NIVEAU SUIVANT — {n} filleuls' }),
  rw_to_level: (level, target) => `vers le niveau ${level} : ${target}`,
  rw_max_level: 'Niveau maximum atteint !',
  rw_badges_section: 'BADGES',
  rw_formats_section: 'FORMATS DE BOX',

  // QUESTION CARD
  qcard_status_new: 'Nouvelle',
  qcard_status_hidden: 'Masquée',
  qcard_status_answered: 'Répondue',
  qcard_moderation_flagged: 'Attention : message suspect',
  qcard_answer_btn: 'Répondre',
  qcard_answer_send: 'Envoyer',
  qcard_share_btn: 'Partager',
  qcard_hide_btn: 'Masquer',
  qcard_show_btn: 'Afficher',
  qcard_report_btn: 'Signaler',
  qcard_block_btn: 'Bloquer',
  qcard_unblock_btn: 'Débloquer',
  qcard_report_reason_label: 'Choisis le motif du signalement :',
  qcard_reason_spam: 'Spam',
  qcard_reason_harassment: 'Harcèlement',
  qcard_reason_sexual: 'Contenu sexuel',
  qcard_reason_other: 'Autre',
  qcard_error_hide: 'Impossible de modifier la visibilité.',
  qcard_error_report: 'Impossible d\'envoyer le signalement.',
  qcard_error_block: 'Impossible de bloquer l\'expéditeur.',
  qcard_report_done: 'Signalement envoyé — merci.',
  qcard_block_done: 'Expéditeur bloqué.',
  qcard_unblock_done: 'Expéditeur débloqué.',

  // ERRORS
  err_network: 'Pas de connexion au serveur. Vérifie ta connexion et réessaie.',
  err_server_unavailable: 'Le serveur est temporairement indisponible, réessaie plus tard.',
  err_internal: 'Quelque chose s\'est mal passé de notre côté. On s\'en occupe — réessaie plus tard.',
  err_bad_request: 'Vérifie les données que tu as saisies.',
  err_generic: 'Impossible d\'effectuer l\'action, réessaie plus tard.',
  err_schema: 'Erreur de données. Essaie de mettre à jour l\'app.',
  share_story_link_premium_note:
    'Un lien cliquable dans les Stories nécessite Telegram Premium. Sans lui, tes amis peuvent quand même te trouver — ton nom d\'utilisateur est affiché sur la carte.',

  // AUTH ERROR SCREEN
  auth_error_title: 'Erreur d\'accès',
  auth_error_desc:
    'Authentification échouée. Ferme et rouvre la Mini App depuis Telegram.',

  // REWARD TOAST
  reward_toast_title: 'Nouvelle récompense !',
  reward_toast_close: '×',

  // BOTTOM TABBAR
  tab_questions: 'Questions',
  tab_leaderboard: 'Classement',
  tab_rewards: 'Récompenses',
  tab_settings: 'Paramètres',

  // SETTINGS SCREEN
  settings_title: 'Paramètres',
  settings_section_general: 'Général',
  settings_notifications: 'Notifications pour les nouvelles questions',
  settings_notifications_hint: 'Le bot t\'enverra un message quand quelqu\'un te pose une question.',
  settings_notifications_error: 'Impossible de sauvegarder le paramètre de notification.',
  settings_language: 'Langue',
  settings_language_error: 'Impossible de changer la langue. Réessaie.',
  settings_section_about: 'À propos',
  settings_version: 'Version',

  // CODE ENTRY (work-020)
  code_modal_title: 'Les liens en Stories sont réservés aux Premium',
  code_modal_explain:
    'Telegram ne donne un lien cliquable en Stories qu\'aux abonnés Premium. À la place, ajoute ton code dans la story — les visiteurs le tapent dans l\'app et te posent une question anonyme.',
  code_modal_your_code: 'Ton code',
  code_modal_copy: 'Copier le code',
  code_modal_copied: 'Copié',
  code_modal_share_btn: 'Partager en story',
  code_modal_loading: 'Récupération du code…',
  code_modal_error: 'Impossible d\'obtenir ton code. Réessaie plus tard.',
  ask_code_title: 'Entre un code',
  ask_code_desc:
    'Tu arrives d\'une story ? Trouve le code dedans (ex. 1234) et tape-le ci-dessous.',
  ask_code_placeholder: 'Code de la story',
  ask_code_find: 'Chercher',
  ask_code_not_found: 'Code introuvable. Vérifie les chiffres et réessaie.',
  ask_code_invalid: 'Le code fait 4 chiffres.',
  code_onboarding_header: 'Tu arrives d\'une story ?',
  code_onboarding_text: 'Entre le code d\'un ami pour lui poser une question anonyme.',
  code_onboarding_action: 'Entrer le code',
  settings_ask_by_code: 'Demander par code',
  settings_my_code_title: 'Ton code pour les stories',
  settings_my_code_hint:
    'Écris ce code dans ta story — les visiteurs le tapent pour te poser une question anonyme. Telegram réserve les liens cliquables en Stories aux abonnés Premium.',
  settings_my_code_rotate: 'Obtenir un nouveau code',
  settings_my_code_rotating: 'Mise à jour…',
  settings_my_code_done: 'Code mis à jour',
  settings_my_code_error: 'Impossible d\'obtenir ton code.',
  settings_my_code_rotate_confirm_title: 'Changer ton code ?',
  settings_my_code_rotate_confirm_desc:
    'Ton ancien code cesse de marcher tout de suite. Les gens qui l\'ont vu dans ta story ne pourront plus t\'écrire. Ne le change que si quelqu\'un te spamme.',
  settings_my_code_rotate_confirm_action: 'Changer le code',

  // BOT
  bot_greeting: (name) => (name ? `Salut, ${name} ! 👋` : 'Salut ! 👋'),
  bot_onboarding:
    'Voici VeylMe — questions anonymes.\n\n' +
    'Ouvre la Mini App, publie une carte « Pose-moi une question anonymement » dans ta Story — ' +
    'et reçois des questions anonymes ici même.\n\n' +
    '⚠️ 18+. Sois respectueux : le harcèlement, les menaces et le contenu interdit ' +
    'ne sont pas tolérés. Anonymat ≠ impunité — les infractions sont signalées à la modération.',
  bot_open_btn: 'Ouvrir VeylMe',
  notify_answer_btn: 'Répondre',
  bot_invite_text:
    'Quelqu\'un a partagé un lien VeylMe.\n\n' +
    'Ouvre la Mini App pour poser une question anonyme :',
  bot_invite_btn: 'Poser une question anonymement',

  // WORK-027 i18n sweep
  tagline: 'Pose-moi une question anonymement',
  share_ask_action: 'Demander',
  share_answered_text: 'J\'ai répondu à une question anonyme 👀',
  err_not_in_telegram: 'La Mini App ne s\'exécute pas dans Telegram. Merci de redémarrer l\'app.',
  badge_viral_starter: 'Démarrage viral',
  badge_storyteller: 'Conteur',
  badge_loop_master: 'Maître de la boucle',
  theme_confession: 'Confessions',
  theme_guesswho: 'Qui est-ce',
  rw_format_named: (name) => `format « ${name} »`,
  card_label_question: 'Question',
  card_label_answer: 'Réponse',
  card_invite_subtitle: 'anonyme, sans inscription',
  card_code_hint: 'ENTRE LE CODE DANS L\'APPLICATION',
  share_msg_title: (brand) => `${brand} · Questions anonymes`,
  notify_new_question: '🔔 Quelqu\'un t\'a posé une question anonyme',
  notify_unanswered_reminder: (n) =>
    plural('fr', n, { one: '🔔 Tu as {n} question sans réponse', other: '🔔 Tu as {n} questions sans réponse' }),
  notify_ban_permanent: 'définitivement',
  notify_ban_until: (date) => `jusqu'au ${date}`,
  notify_ban_msg: (untilPhrase) =>
    `⛔ Tu es bloqué sur VeylMe ${untilPhrase}. Trop de destinataires ont signalé tes messages.\nSi tu penses qu'il s'agit d'une erreur, tu peux soumettre un appel dans l'application.`,
  notify_warning_msg:
    '⚠️ Plusieurs destinataires ont signalé tes messages. Encore un peu et tu seras bloqué sur VeylMe.',

  // MY QUESTIONS SCREEN + InvitePanel non-premium hint (retention-answer-loop)
  my_questions_title: 'Mes questions',
  my_questions_empty_title: 'Tu n\'as pas encore posé de question',
  my_questions_empty_desc: 'Pose une question dans la boîte de quelqu\'un — la réponse apparaîtra ici.',
  my_questions_loading: 'Vérification de la réponse…',
  my_questions_waiting: 'En attente de réponse',
  my_questions_answer_label: 'Réponse',
  settings_my_questions: 'Mes questions',
  ask_sent_find_reply_hint: 'Tu retrouveras la réponse plus tard dans «Mes questions».',
  inbox_story_nonpremium_hint: 'Un lien de Story n\'est pas cliquable sans Telegram Premium, on affiche donc un code — partager dans un chat est plus fiable.',

  // SESSION EXPIRY SCREEN (work-038)
  session_expired_title: 'Ton accès Telegram a expiré',
  session_expired_desc: 'Telegram ne donne son accès à l\'app qu\'une seule fois au lancement, et celui-ci a expiré. Rouvre l\'app — on a gardé le texte que tu avais commencé.',
  session_expired_desc_no_storage: 'Ton accès Telegram a expiré, il faut rouvrir l\'app. On n\'a pas pu enregistrer le texte que tu avais tapé — copie-le avant de partir.',
  session_expired_reopen: 'Rouvrir',
  session_expired_stuck_title: 'Telegram a renvoyé le même ancien accès',
  session_expired_stuck_desc: 'Ferme complètement l\'app depuis le menu du bot, puis rouvre-la — Telegram donnera alors un nouvel accès.',
  session_auth_failed_title: 'Impossible de confirmer ta connexion',
  session_auth_failed_desc: 'Telegram n\'a pas confirmé ta connexion à l\'app. Ferme l\'app et rouvre-la depuis le chat avec le bot.',
  session_expired_manual_close: 'L\'app ne peut pas se fermer toute seule ici — ferme-la toi-même et rouvre-la depuis le chat avec le bot.',
  session_expiring_soon: 'Ton accès Telegram expire bientôt. Rouvre l\'app pour ne rien perdre.',
  my_questions_check_failed: 'Impossible de vérifier les réponses. Réessaie plus tard.',
  settings_notifications_load_error: 'Impossible de charger le réglage.',
};
